/* eslint-disable no-unreachable */
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import Banner from 'components/informative-animated-banner';
import { Container, ErrorState, LoadingOlist } from 'components/layout';
import { AlertContext } from 'contexts/alert-context';
import { AuthContext } from 'contexts/auth';
import { reload } from 'helpers/window-utils';
import useMixPanel from 'hooks/use-mixpanel';
import usePersonalInformation from 'hooks/use-personal-information';
import usePersonalInformationSteps from 'hooks/use-personal-information-steps';
import { useTranslation } from 'react-i18next';
import { Switch, useHistory, useLocation } from 'react-router-dom';
import PrivateRoute from 'routes/private-route';
import Api from 'service';
import {
  COMPANY_ACCOUNT_TAG,
  handleNextStepBySellerTag,
} from 'utils/first-steps-validation/first-steps-validation';

import { Box, Button, Flex } from '@olist/united';

import { bannerData } from './banner-data';
import CompanyInformationForm from './company-information-form';
import { mixPanelEventTitles } from './constants';
import LiablePartnerForm from './liable-partner-form/liable-partner-form';
import LogisticAddressForm from './logistic-address-form/logistic-address-form';
import PersonalInformationWrapper from './personal-information-wrapper';
import { ROUTES_MATCH_PATHS } from './utils';

const PersonalInformationPage = () => {
  const { t } = useTranslation();

  const history = useHistory();
  const { setAlert } = useContext(AlertContext);
  const { isGtmLoaded } = useContext(AuthContext);

  const { track } = useMixPanel();

  const [isSubmittingForm, setSubmittingForm] = useState(false);
  const [isValidForm, setIsValidForm] = useState(false);
  const [personalData, setPersonalData] = useState(null);
  const [isCheckingSellerTag, setIsCheckingSellerTag] = useState(false);
  const [showFormAddress, setShowFormAddress] = useState(false);
  const [hasHubCoverage, setHasHubCoverage] = useState(false);

  const { pathname: currentPathName } = useLocation();
  const stepperController = usePersonalInformationSteps(currentPathName);

  const {
    data: fetchedPersonalData,
    hasError: hasRequestError,
    isLoading: isContentLoading,
  } = usePersonalInformation();

  useEffect(() => {
    if (!personalData && fetchedPersonalData) {
      setPersonalData({ ...fetchedPersonalData, toggleAddressNumber: false });
    }
  }, [fetchedPersonalData, personalData]);

  const formSubmitHandlerRef = useRef();

  const setSubmitHandlerCallback = (submitCallback, isValid, submitting) => {
    setTimeout(() => setIsValidForm(isValid), 0);
    setTimeout(() => setSubmittingForm(submitting), 0);
    formSubmitHandlerRef.current = submitCallback;
  };

  const handleNextButtonClick = () => {
    setSubmittingForm(true);
    formSubmitHandlerRef.current?.();
  };

  const goToNextStep = useCallback(
    (path) => {
      const eventsToTracks = [
        mixPanelEventTitles.submit_logistic_address,
        mixPanelEventTitles.submit_company_information,
        mixPanelEventTitles.submit_liable_partner,
      ];

      track(eventsToTracks[stepperController.currentStep]);
      setSubmittingForm(false);
      history.push(path ?? stepperController.nextStepPath);
    },
    [history, stepperController.currentStep, stepperController.nextStepPath, track]
  );

  const checkIsSellerTagCreated = useCallback(async () => {
    try {
      const responseValues = await Api.getSellerTags();
      const isCompletedCompanyAccount =
        responseValues && responseValues.sellerTags.includes(COMPANY_ACCOUNT_TAG);
      if (isCompletedCompanyAccount) {
        const redirectValue = handleNextStepBySellerTag(responseValues.sellerTags);
        goToNextStep(redirectValue);
        setIsCheckingSellerTag(false);
      }
    } catch (error) {
      setIsCheckingSellerTag(false);
      setAlert({
        text: t`An error occurred and the information could not be sent. Try again.`,
        show: true,
        iconName: 'alert-triangle-outline',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goToNextStep, setAlert, setIsCheckingSellerTag]);

  useEffect(() => {
    if (isCheckingSellerTag) {
      checkIsSellerTagCreated();
      const intervalPolling = setInterval(async () => {
        checkIsSellerTagCreated();
      }, 2500);

      return () => clearInterval(intervalPolling);
    }

    return null;
  }, [checkIsSellerTagCreated, isCheckingSellerTag]);

  if (hasRequestError) {
    return (
      <Flex height="100vh" justifyContent="center" alignItems="center">
        <ErrorState onClick={() => reload()} />
      </Flex>
    );
  }

  if (!personalData || isContentLoading || isCheckingSellerTag) {
    return <LoadingOlist />;
  }

  const defaultFormsProps = (initialValues) => ({
    setSubmitCallback: setSubmitHandlerCallback,
    updatePersonalData: setPersonalData,
    goToNextStep,
    initialValues,
    isGtmLoaded,
    showFormAddress,
    setShowFormAddress,
    setHasHubCoverage,
  });

  const showDefaultBanner = stepperController.currentStep === 1 && !showFormAddress;
  const showHasHubCoverageBanner = showFormAddress && hasHubCoverage;
  const showDoesNotHaveHubCoverageBanner = showFormAddress && !hasHubCoverage;
  const stepLabel = t`step ${stepperController.currentStep} of ${stepperController.totalSteps}`;
  const showBackBtn = stepperController.currentStep > 1;

  return (
    <Container>
      <Flex
        justifyContent="space-between"
        flexDirection={['column', 'column', 'column', 'row']}
        mb={3}
      >
        <Flex
          flex={1}
          flexDirection={['column']}
          width={1}
          maxWidth="660px"
          minHeight="calc(100vh - 134px)"
        >
          <Flex flexDirection="column">
            <Switch>
              <PrivateRoute path={ROUTES_MATCH_PATHS.logisticAddress.path} exact>
                <PersonalInformationWrapper stepLabel={stepLabel} title={t`logistic address`}>
                  <LogisticAddressForm
                    {...defaultFormsProps(personalData?.address)}
                    showFormAddress={showFormAddress}
                    hasHubCoverage={hasHubCoverage}
                    setShowFormAddress={setShowFormAddress}
                    setHasHubCoverage={setHasHubCoverage}
                  />
                </PersonalInformationWrapper>
              </PrivateRoute>
              <PrivateRoute path={ROUTES_MATCH_PATHS.companyInformation.path} exact>
                <PersonalInformationWrapper stepLabel={stepLabel} title={t`company data`}>
                  <CompanyInformationForm {...defaultFormsProps(personalData?.company)} />
                </PersonalInformationWrapper>
              </PrivateRoute>
              <PrivateRoute path={ROUTES_MATCH_PATHS.liablePartner.path} exact>
                <PersonalInformationWrapper
                  stepLabel={stepLabel}
                  title={t`liable partner`}
                  description={t`We will use this information to contact you whenever necessary`}
                >
                  <LiablePartnerForm
                    {...defaultFormsProps(personalData?.responsible)}
                    setIsCheckingSellerTag={setIsCheckingSellerTag}
                    isCheckingSellerTag={isCheckingSellerTag}
                  />
                </PersonalInformationWrapper>
              </PrivateRoute>
            </Switch>
            <Flex flexDirection="column" mt={4}>
              <Flex flex="1" justifyContent="flex-end">
                {showBackBtn && (
                  <Button
                    type="button"
                    disabled={isSubmittingForm}
                    onClick={() => history.goBack()}
                  >{t`back`}</Button>
                )}
                <Button
                  ml={3}
                  onClick={() => handleNextButtonClick()}
                  loading={isSubmittingForm}
                  disabled={!isValidForm}
                  variation="alternate"
                >{t`continue`}</Button>
              </Flex>
            </Flex>
          </Flex>
          <Box
            display={['block', 'block', 'block', 'none']}
            minHeight="120px"
            backgroundColor="transparent"
          />
        </Flex>
        <Box
          mt={9}
          paddingLeft={8}
          flexDirection={['column']}
          alignItems="flex-end"
          display={['none', 'none', 'none', 'none', 'block']}
        >
          {showDefaultBanner && <Banner {...bannerData[0]} />}
          {showHasHubCoverageBanner && <Banner {...bannerData[1]} />}
          {showDoesNotHaveHubCoverageBanner && <Banner {...bannerData[2]} />}
        </Box>
      </Flex>
    </Container>
  );
};

export default PersonalInformationPage;
